import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Star, Minus, Plus, ShoppingBag, Check, ShieldCheck, Truck, RotateCcw } from 'lucide-react';
import { products, ProductColor, ProductAddOn, FALLBACK_PRODUCT_IMAGE } from '../data/products';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ProductCard } from '../components/ProductCard';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    dispatch
  } = useCart();
  const product = products.find(p => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | undefined>(product?.sizes?.[0]);
  const [selectedColor, setSelectedColor] = useState<ProductColor | undefined>(product?.colors?.[0]);
  const [selectedAddOns, setSelectedAddOns] = useState<ProductAddOn[]>([]);
  const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'reviews'>('description');
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  }, [product]);
  if (!product) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center" data-id="element-502">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-503">
          Product not found
        </h2>
        <button type="button" onClick={() => navigate('/shop')} className="text-red-600 hover:underline" data-id="element-504">
          Return to Shop
        </button>
      </div>;
  }
  const basePrice = product.salePrice || product.price;
  const addOnsTotal = selectedAddOns.reduce((sum, addon) => sum + addon.priceDelta, 0);
  const unitPrice = basePrice + addOnsTotal;
  const handleAddOnToggle = (addon: ProductAddOn) => {
    setSelectedAddOns(prev => prev.some(a => a.id === addon.id) ? prev.filter(a => a.id !== addon.id) : [...prev, addon]);
  };
  const handleAddToCart = () => {
    if (product.sizes && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (product.colors && !selectedColor) {
      toast.error('Please select a color');
      return;
    }
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: `${product.id}-${selectedSize || ''}-${selectedColor?.name || ''}-${selectedAddOns.map(a => a.id).join('-')}`,
        productId: product.id,
        product,
        quantity,
        selectedSize,
        selectedColor,
        selectedAddOns,
        unitPrice
      }
    });
    toast.success('Added to cart', {
      description: `${quantity}x ${product.name} added to your cart.`
    });
  };
  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-505">
      {/* Breadcrumbs */}
      <nav className="flex text-sm text-gray-500 mb-8" aria-label="Breadcrumb" data-id="element-506">
        <ol className="flex items-center space-x-2" data-id="element-507">
          <li data-id="element-508">
            <Link to="/" className="hover:text-gray-900" data-id="element-509">
              Home
            </Link>
          </li>
          <li data-id="element-510">
            <ChevronRight className="w-4 h-4" data-id="element-511" />
          </li>
          <li data-id="element-512">
            <Link to="/shop" className="hover:text-gray-900" data-id="element-513">
              Shop
            </Link>
          </li>
          <li data-id="element-514">
            <ChevronRight className="w-4 h-4" data-id="element-515" />
          </li>
          <li data-id="element-516">
            <Link to={`/shop/${product.category}`} className="hover:text-gray-900 capitalize" data-id="element-517">
              {product.category}
            </Link>
          </li>
          <li data-id="element-518">
            <ChevronRight className="w-4 h-4" data-id="element-519" />
          </li>
          <li className="text-gray-900 font-medium" aria-current="page" data-id="element-520">
            {product.name}
          </li>
        </ol>
      </nav>

      <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16" data-id="element-521">
        {/* Image Gallery */}
        <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-6 mb-10 lg:mb-0" data-id="element-522">
          {product.images.length > 1 && <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible py-2 lg:py-0 lg:w-24 flex-shrink-0" data-id="element-523">
              {product.images.map((img, idx) => <button key={idx} type="button" onClick={() => setSelectedImage(idx)} className={`relative aspect-square rounded-lg overflow-hidden border-2 flex-shrink-0 w-20 lg:w-full ${selectedImage === idx ? 'border-red-600' : 'border-transparent hover:border-gray-300'}`} data-id="element-524">
                  <img src={img} alt={`${product.name} thumbnail ${idx + 1}`} onError={e => {
              const el = e.currentTarget;
              if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
            }} className="w-full h-full object-cover" data-id="element-525" />
                </button>)}
            </div>}

          <div className="relative aspect-[4/5] lg:aspect-auto lg:h-[600px] w-full bg-gray-100 rounded-2xl overflow-hidden" data-id="element-526">
            <img src={product.images[selectedImage]} alt={product.name} onError={e => {
            const el = e.currentTarget;
            if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
          }} className="w-full h-full object-cover object-center" data-id="element-527" />
            {product.salePrice && <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md uppercase tracking-wider" data-id="element-528">
                Sale
              </div>}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex flex-col" data-id="element-529">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-530">
            {product.name}
          </h1>

          <div className="flex items-center gap-4 mb-6" data-id="element-531">
            <div className="flex items-center gap-1" data-id="element-532">
              {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-533" />)}
            </div>
            <span className="text-sm text-gray-500" data-id="element-534">
              {product.rating} ({product.reviewsCount} reviews)
            </span>
          </div>

          <div className="flex items-end gap-3 mb-6" data-id="element-535">
            <span className="text-3xl font-bold text-gray-900" data-id="element-536">
              MVR {unitPrice.toFixed(2)}
            </span>
            {product.salePrice && addOnsTotal === 0 && <span className="text-lg text-gray-400 line-through mb-1" data-id="element-537">
                MVR {product.price.toFixed(2)}
              </span>}
          </div>

          <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-538">
            {product.description}
          </p>

          <div className="space-y-8 mb-8" data-id="element-539">
            {/* Colors */}
            {product.colors && product.colors.length > 0 && <div data-id="element-540">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-541">
                  Color:{' '}
                  <span className="text-gray-500" data-id="element-542">{selectedColor?.name}</span>
                </h3>
                <div className="flex items-center gap-3 flex-wrap" data-id="element-543">
                  {product.colors.map(color => {
                const isSelected = selectedColor?.name === color.name;
                return <button key={color.name} type="button" onClick={() => setSelectedColor(color)} aria-label={`Select color ${color.name}`} aria-pressed={isSelected} className={`relative w-10 h-10 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all ${isSelected ? 'ring-2 ring-offset-2 ring-gray-900' : 'ring-1 ring-gray-200 hover:ring-gray-400'}`} style={{
                  backgroundColor: color.hex
                }} title={color.name} data-id="element-544">
                        {isSelected && <Check className={`w-5 h-5 ${['#ffffff', '#f8fafc', '#f5f5f4'].includes(color.hex.toLowerCase()) ? 'text-gray-900' : 'text-white'}`} data-id="element-545" />}
                      </button>;
              })}
                </div>
              </div>}

            {/* Sizes */}
            {product.sizes && product.sizes.length > 0 && <div data-id="element-546">
                <div className="flex justify-between items-center mb-3" data-id="element-547">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-548">Size</h3>
                  <button type="button" className="text-sm text-red-600 hover:underline" data-id="element-549">
                    Size Guide
                  </button>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" data-id="element-550">
                  {product.sizes.map(size => {
                const stock = product.stockBySize?.[size] ?? 10;
                const isOutOfStock = stock === 0;
                const isSelected = selectedSize === size;
                return <button key={size} type="button" disabled={isOutOfStock} onClick={() => setSelectedSize(size)} aria-pressed={isSelected} className={`
                          py-2.5 text-sm font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors
                          ${isOutOfStock ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed line-through' : isSelected ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-gray-300 hover:border-gray-900'}
                        `} data-id="element-551">
                        {size}
                      </button>;
              })}
                </div>
                {selectedSize && product.stockBySize && product.stockBySize[selectedSize] <= 5 && product.stockBySize[selectedSize] > 0 && <p className="mt-2 text-sm text-orange-600 flex items-center gap-1" data-id="element-552">
                      Only {product.stockBySize[selectedSize]} left in stock!
                    </p>}
              </div>}

            {/* Add-ons */}
            {product.addOns && product.addOns.length > 0 && <div data-id="element-553">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-554">
                  Extras
                </h3>
                <div className="space-y-3" data-id="element-555">
                  {product.addOns.map(addon => {
                const isSelected = selectedAddOns.some(a => a.id === addon.id);
                return <button key={addon.id} type="button" onClick={() => handleAddOnToggle(addon)} aria-pressed={isSelected} className={`w-full flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${isSelected ? 'border-red-600 bg-red-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-556">
                        <div className="flex items-center gap-3" data-id="element-557">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-red-600 border-red-600' : 'border-gray-300 bg-white'}`} data-id="element-558">
                            {isSelected && <Check className="w-3.5 h-3.5 text-white" data-id="element-559" />}
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-560">
                            {addon.name}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500" data-id="element-561">
                          +MVR {addon.priceDelta.toFixed(2)}
                        </span>
                      </button>;
              })}
                </div>
              </div>}

            {/* Quantity */}
            <div data-id="element-562">
              <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-563">
                Quantity
              </h3>
              <div className="flex items-center w-32 border border-gray-300 rounded-lg bg-white" data-id="element-564">
                <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg" aria-label="Decrease quantity" data-id="element-565">
                  <Minus className="w-4 h-4" data-id="element-566" />
                </button>
                <span className="flex-1 text-center font-medium text-gray-900" data-id="element-567">
                  {quantity}
                </span>
                <button type="button" onClick={() => setQuantity(quantity + 1)} className="p-3 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg" aria-label="Increase quantity" data-id="element-568">
                  <Plus className="w-4 h-4" data-id="element-569" />
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-570">
            <button type="button" onClick={handleAddToCart} className="flex-1 bg-white text-gray-900 border-2 border-gray-900 py-3.5 px-6 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-id="element-571">
              <ShoppingBag className="w-5 h-5" data-id="element-572" /> Add to Cart
            </button>
            <button type="button" onClick={handleBuyNow} className="flex-1 bg-red-600 text-white py-3.5 px-6 rounded-xl font-medium hover:bg-red-700 transition-colors" data-id="element-573">
              Buy Now
            </button>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 gap-4 py-6 border-t border-gray-200" data-id="element-574">
            <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-575">
              <Truck className="w-5 h-5 text-gray-400" data-id="element-576" />
              <span data-id="element-577">Free delivery over MVR 500</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-578">
              <RotateCcw className="w-5 h-5 text-gray-400" data-id="element-579" />
              <span data-id="element-580">14-day easy returns</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-581">
              <ShieldCheck className="w-5 h-5 text-gray-400" data-id="element-582" />
              <span data-id="element-583">Secure BML checkout</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="mt-16 border-t border-gray-200 pt-10" data-id="element-584">
        <div className="flex space-x-8 border-b border-gray-200 mb-8" data-id="element-585">
          <button type="button" onClick={() => setActiveTab('description')} className={`pb-4 text-sm font-medium transition-colors relative ${activeTab === 'description' ? 'text-red-600' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-586">
            Description
            {activeTab === 'description' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-red-600 rounded-t-md" data-id="element-587" />}
          </button>
          <button type="button" onClick={() => setActiveTab('reviews')} className={`pb-4 text-sm font-medium transition-colors relative ${activeTab === 'reviews' ? 'text-red-600' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-588">
            Reviews ({product.reviewsCount})
            {activeTab === 'reviews' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-red-600 rounded-t-md" data-id="element-589" />}
          </button>
        </div>

        <div className="prose prose-sm sm:prose max-w-none text-gray-600" data-id="element-590">
          {activeTab === 'description' && <div data-id="element-591">
              <p data-id="element-592">{product.description}</p>
              <p className="mt-4" data-id="element-593">
                Designed for the perfect Maldivian beach day, this item combines
                style with functionality. Made with premium materials to
                withstand sun, sand, and saltwater.
              </p>
            </div>}
          {activeTab === 'reviews' && <div className="space-y-8" data-id="element-594">
              <div className="flex items-center gap-4 mb-8" data-id="element-595">
                <div className="text-4xl font-bold text-gray-900" data-id="element-596">
                  {product.rating}
                </div>
                <div data-id="element-597">
                  <div className="flex items-center gap-1 mb-1" data-id="element-598">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-599" />)}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-600">
                    Based on {product.reviewsCount} reviews
                  </div>
                </div>
              </div>
              <div className="border-b border-gray-100 pb-6" data-id="element-601">
                <div className="flex items-center gap-2 mb-2" data-id="element-602">
                  <div className="flex items-center gap-1" data-id="element-603">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-604" />)}
                  </div>
                  <span className="font-medium text-gray-900" data-id="element-605">
                    Great quality!
                  </span>
                </div>
                <p className="text-gray-600 mb-2" data-id="element-606">
                  Exactly what I was looking for. The quality is excellent and
                  it arrived quickly.
                </p>
                <div className="text-sm text-gray-400" data-id="element-607">
                  Ahmed M. — 2 weeks ago
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && <div className="mt-16 border-t border-gray-200 pt-10" data-id="element-608">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-609">
            You may also like
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-610">
            {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-611" />)}
          </div>
        </div>}
    </div>;
}